/**
 * gammaRolloutRouter.ts — MER Algo Footprints v7.1B — Phase 6.7
 *
 * Pure rollout decision router for Gamma promotion profiles.
 *
 * ── Purpose ───────────────────────────────────────────────────────────────────
 * Given a promotion profile + evaluation context (symbol, setupType), determines:
 *   - which flags to apply (baseline or candidate)
 *   - what rollout mode is in effect
 *   - whether the candidate is in shadow mode (observe only, no scoring effect)
 *
 * ── Design ────────────────────────────────────────────────────────────────────
 * Pure function — no side effects, no I/O, no mutation.
 * All routing decisions are explicit and logged in the result for auditability.
 *
 * ── Shadow mode guarantee ────────────────────────────────────────────────────
 * When rolloutMode = 'shadow', the returned effectiveFlags are always the
 * BASELINE flags (no scoring effect). The candidateFlags field carries what the
 * candidate would have applied for parallel attribution capture.
 *
 * Placement: src/domains/market/engines/gamma/gammaRolloutRouter.ts
 */

import type { GammaPromotionProfile } from './gammaPromotionProfile';
import type { GammaInfluenceFlags }   from './gammaInfluenceFlags';
import { BASELINE_PROFILE }           from './gammaPromotionProfile';

// ─── Routing result ───────────────────────────────────────────────────────────

export type RolloutDecision =
  | 'baseline'           // candidate inactive/retired — baseline applies
  | 'shadow'             // candidate in shadow mode — baseline applies to scoring
  | 'limited_candidate'  // candidate applies because symbol/setupType is in scope
  | 'limited_baseline'   // limited candidate but this context is out of scope
  | 'active_candidate';  // candidate fully active

export interface RolloutRoutingResult {
  rolloutDecision:  RolloutDecision;
  rolloutMode:      GammaPromotionProfile['status'];
  /** The flags to use for LIVE scoring. */
  effectiveFlags:   GammaInfluenceFlags;
  /** The candidate's flags, even if not applied to live scoring (for shadow observation). */
  candidateFlags:   GammaInfluenceFlags | null;
  profileId:        string;
  symbol:           string;
  setupType:        string;
  reason:           string;
}

// ─── Scope matching ───────────────────────────────────────────────────────────

function matchesScope(
  symbol:    string,
  setupType: string,
  profile:   GammaPromotionProfile,
): boolean {
  const { scope } = profile;
  if (!scope) return true;   // no scope restriction

  const symbolMatch = (!scope.symbols || scope.symbols.length === 0)
    || scope.symbols.some(s => s.toUpperCase() === symbol.toUpperCase());

  const setupMatch = (!scope.setupTypes || scope.setupTypes.length === 0)
    || scope.setupTypes.some(t => setupType.toLowerCase().includes(t.toLowerCase()));

  return symbolMatch && setupMatch;
}

// ─── Main router ──────────────────────────────────────────────────────────────

/**
 * Determines which Gamma flags apply for a given context and candidate profile.
 *
 * Routing rules (in priority order):
 *   1. candidate is baseline → always baseline
 *   2. candidate is inactive or retired → always baseline
 *   3. candidate is shadow → effectiveFlags = baseline (scoring), candidateFlags = candidate (observation)
 *   4. candidate is limited → apply candidate iff context is in scope; otherwise baseline
 *   5. candidate is active → apply candidate to all contexts
 *
 * @param candidate  The promotion profile to evaluate (may be baseline itself)
 * @param symbol     The symbol being evaluated
 * @param setupType  The setup type string for scope matching
 */
export function resolveRollout(
  candidate: GammaPromotionProfile,
  symbol:    string,
  setupType: string,
): RolloutRoutingResult {
  const base: Omit<RolloutRoutingResult, 'rolloutDecision' | 'effectiveFlags' | 'candidateFlags' | 'reason'> = {
    rolloutMode:   candidate.status,
    profileId:     candidate.id,
    symbol,
    setupType,
  };

  // 1. Baseline profile — always returns baseline flags
  if (candidate.isBaseline || candidate.id === 'baseline') {
    return {
      ...base,
      rolloutDecision: 'baseline',
      rolloutMode:     'active',
      effectiveFlags:  BASELINE_PROFILE.flags,
      candidateFlags:  null,
      reason: 'Baseline profile is always active.',
    };
  }

  // 2. Inactive or retired — fall back to baseline
  if (candidate.status === 'inactive' || candidate.status === 'retired') {
    return {
      ...base,
      rolloutDecision: 'baseline',
      effectiveFlags:  BASELINE_PROFILE.flags,
      candidateFlags:  null,
      reason: `Candidate '${candidate.id}' is ${candidate.status} — baseline applies.`,
    };
  }

  // 3. Shadow — baseline to scoring, candidate for observation only
  if (candidate.status === 'shadow') {
    return {
      ...base,
      rolloutDecision: 'shadow',
      effectiveFlags:  BASELINE_PROFILE.flags,   // ← baseline for live scoring
      candidateFlags:  candidate.flags,           // ← candidate for parallel observation
      reason: `Candidate '${candidate.id}' is in shadow mode — baseline applies to scoring; candidate captured for observation.`,
    };
  }

  // 4. Limited — apply candidate only if in scope
  if (candidate.status === 'limited') {
    const inScope = matchesScope(symbol, setupType, candidate);
    if (inScope) {
      return {
        ...base,
        rolloutDecision: 'limited_candidate',
        effectiveFlags:  candidate.flags,
        candidateFlags:  candidate.flags,
        reason: `Candidate '${candidate.id}' limited-mode applies: symbol='${symbol}' setupType='${setupType}' is in scope.`,
      };
    } else {
      return {
        ...base,
        rolloutDecision: 'limited_baseline',
        effectiveFlags:  BASELINE_PROFILE.flags,
        candidateFlags:  candidate.flags,
        reason: `Candidate '${candidate.id}' limited-mode: symbol='${symbol}' setupType='${setupType}' is out of scope — baseline applies.`,
      };
    }
  }

  // 5. Active — apply candidate to all contexts
  return {
    ...base,
    rolloutDecision: 'active_candidate',
    effectiveFlags:  candidate.flags,
    candidateFlags:  candidate.flags,
    reason: `Candidate '${candidate.id}' is fully active.`,
  };
}

/**
 * Convenience: resolves rollout using the current active profile from the registry.
 * Callers should pass the result of getActiveProfile() as candidate.
 */
export function resolveActiveRollout(
  candidate: GammaPromotionProfile,
  symbol:    string,
  setupType: string,
): RolloutRoutingResult {
  return resolveRollout(candidate, symbol, setupType);
}
